import { PALETTE, sline, scircle, srect, fillPoly, rand, wob } from './sketch.js';

/**
 * 瓦片简笔画绘制。
 * 每种瓦片预渲染 4 个手绘变体到离屏 canvas，运行时只做 drawImage，
 * 避免每帧重算抖动路径（性能 + 稳定性）。
 */

export const TILE_DEFS = {
  void: { name: '虚空', solid: true },
  wall: { name: '墙', solid: true },
  wood: { name: '木地板', solid: false },
  grass: { name: '草地', solid: false },
  soil: { name: '土地', solid: false },
  stone: { name: '石板', solid: false },
  door: { name: '门槛', solid: false },
  water: { name: '水', solid: true },
  /* ---- 野外 ---- */
  dirt: { name: '泥地', solid: false },
  field: { name: '农田', solid: false },
  sand: { name: '河沙', solid: false },
  water_edge: { name: '水岸', solid: false },
  /* ---- 道路 ---- */
  road: { name: '土路', solid: false },
  road_line: { name: '车道', solid: false },
  bridge: { name: '木桥', solid: false },
  checkpoint: { name: '关卡石', solid: false },
  /* ---- 城镇 ---- */
  sidewalk: { name: '街砖', solid: false },
  plaza: { name: '广场石', solid: false },
  building: { name: '屋基', solid: true },
};

/**
 * 通用字符画映射：所有场景共用一套字符，新增地图不必重复造字典。
 * 场景自己的 TILE_CHARS 若与此表冲突，以场景为准。
 */
export const MAP_CHARS = {
  ' ': 'void',
  '#': 'wall',
  '.': 'wood',
  ',': 'grass',
  ':': 'soil',
  '~': 'stone',
  '+': 'door',
  'w': 'water',
  'e': 'water_edge',
  's': 'sand',
  'd': 'dirt',
  'f': 'field',
  'r': 'road',
  '/': 'road_line',
  'n': 'bridge',
  '^': 'checkpoint',
  '=': 'sidewalk',
  'o': 'plaza',
  'b': 'building',
};

const painters = {
  void(ctx, s) {
    fillPoly(ctx, [[0, 0], [s, 0], [s, s], [0, s]], '#2c2a27');
  },

  wall(ctx, s, seed) {
    fillPoly(ctx, [[0, 0], [s, 0], [s, s], [0, s]], '#e3d8c2');
    // 砖缝
    const rows = 3;
    const rh = s / rows;
    for (let r = 0; r < rows; r++) {
      const y = r * rh;
      sline(ctx, 0, y, s, y, { color: PALETTE.inkSoft, width: 1, alpha: 0.55, seed: seed + r, amp: 0.6 });
      const off = (r % 2) * (s / 4);
      for (let c = 0; c < 2; c++) {
        const x = off + c * (s / 2) + s / 4;
        sline(ctx, x, y, x, y + rh, { color: PALETTE.inkSoft, width: 1, alpha: 0.4, seed: seed + r * 7 + c, amp: 0.5 });
      }
    }
    srect(ctx, 0.5, 0.5, s - 1, s - 1, { color: PALETTE.ink, width: 1.2, alpha: 0.5, seed: seed + 99 });
  },

  wood(ctx, s, seed) {
    fillPoly(ctx, [[0, 0], [s, 0], [s, s], [0, s]], '#e8d3ac');
    // 拼板缝
    const n = 2;
    for (let i = 1; i < n; i++) {
      const y = (s / n) * i;
      sline(ctx, 0, y, s, y, { color: PALETTE.woodDark, width: 1, alpha: 0.5, seed: seed + i, amp: 0.7 });
    }
    // 木纹
    for (let i = 0; i < 2; i++) {
      const y = rand(seed + i) * s;
      sline(ctx, 2, y, s - 2, y + wob(seed + i + 5, 2), { color: PALETTE.woodDark, width: 0.8, alpha: 0.3, seed: seed + i + 11, amp: 1.5 });
    }
    srect(ctx, 0.5, 0.5, s - 1, s - 1, { color: PALETTE.woodDark, width: 1, alpha: 0.35, seed: seed + 31 });
  },

  grass(ctx, s, seed) {
    fillPoly(ctx, [[0, 0], [s, 0], [s, s], [0, s]], '#dfe6c8');
    // 草叶小簇
    const n = 5;
    for (let i = 0; i < n; i++) {
      const x = rand(seed + i * 3) * s;
      const y = rand(seed + i * 5 + 1) * s;
      const h = 3 + rand(seed + i * 7) * 3;
      sline(ctx, x, y, x - 1.5, y - h, { color: PALETTE.leaf, width: 1.2, seed: seed + i, amp: 0.4 });
      sline(ctx, x, y, x + 1.5, y - h * 0.8, { color: PALETTE.leafDark, width: 1, seed: seed + i + 40, amp: 0.4 });
    }
  },

  soil(ctx, s, seed) {
    fillPoly(ctx, [[0, 0], [s, 0], [s, s], [0, s]], '#d7bb95');
    // 犁沟
    for (let i = 0; i < 3; i++) {
      const y = 6 + i * (s / 3);
      sline(ctx, 3, y, s - 3, y + wob(seed + i, 1.5), { color: PALETTE.soilDark, width: 1, alpha: 0.45, seed: seed + i, amp: 1 });
    }
    // 土粒
    for (let i = 0; i < 4; i++) {
      const x = rand(seed + i * 9) * s;
      const y = rand(seed + i * 11 + 3) * s;
      scircle(ctx, x, y, 1, { color: PALETTE.soilDark, width: 0.8, alpha: 0.5, seed: seed + i, seg: 5, amp: 0.2 });
    }
  },

  stone(ctx, s, seed) {
    fillPoly(ctx, [[0, 0], [s, 0], [s, s], [0, s]], '#ded9d0');
    const half = s / 2;
    sline(ctx, half, 0, half, s, { color: PALETTE.inkSoft, width: 1, alpha: 0.35, seed: seed + 1, amp: 0.8 });
    sline(ctx, 0, half, s, half, { color: PALETTE.inkSoft, width: 1, alpha: 0.35, seed: seed + 2, amp: 0.8 });
    scircle(ctx, half * 0.6, half * 0.6, 1.2, { color: PALETTE.inkSoft, width: 0.8, alpha: 0.4, seed: seed + 3, seg: 5 });
  },

  door(ctx, s, seed) {
    fillPoly(ctx, [[0, 0], [s, 0], [s, s], [0, s]], '#caa878');
    // 门框
    sline(ctx, 2, 0, 2, s, { color: PALETTE.woodDark, width: 2, seed: seed + 1, amp: 0.5 });
    sline(ctx, s - 2, 0, s - 2, s, { color: PALETTE.woodDark, width: 2, seed: seed + 2, amp: 0.5 });
  },

  water(ctx, s, seed) {
    fillPoly(ctx, [[0, 0], [s, 0], [s, s], [0, s]], '#bcd9e0');
    for (let i = 0; i < 2; i++) {
      const y = 10 + i * 14;
      sline(ctx, 5, y, s - 5, y, { color: PALETTE.water, width: 1.4, alpha: 0.7, seed: seed + i, amp: 2 });
    }
  },

  /* ------------------------------ 野外 ------------------------------ */

  dirt(ctx, s, seed) {
    fillPoly(ctx, [[0, 0], [s, 0], [s, s], [0, s]], '#d9c9a6');
    for (let i = 0; i < 5; i++) {
      const x = rand(seed + i * 3) * s;
      const y = rand(seed + i * 7 + 1) * s;
      scircle(ctx, x, y, 1 + rand(seed + i) * 1.4, { color: PALETTE.soilDark, width: 0.8, alpha: 0.45, seed: seed + i, seg: 5, amp: 0.3 });
    }
  },

  field(ctx, s, seed) {
    fillPoly(ctx, [[0, 0], [s, 0], [s, s], [0, s]], '#cdb187');
    for (let i = 0; i < 5; i++) {
      const y = 4 + i * (s / 5);
      sline(ctx, 1, y, s - 1, y + wob(seed + i, 1.2), { color: PALETTE.soilDark, width: 1, alpha: 0.5, seed: seed + i, amp: 0.8 });
    }
  },

  sand(ctx, s, seed) {
    fillPoly(ctx, [[0, 0], [s, 0], [s, s], [0, s]], '#ece0bd');
    for (let i = 0; i < 6; i++) {
      const x = rand(seed + i * 5) * s;
      const y = rand(seed + i * 11 + 2) * s;
      scircle(ctx, x, y, 0.9, { color: PALETTE.inkSoft, width: 0.6, alpha: 0.3, seed: seed + i, seg: 4 });
    }
  },

  water_edge(ctx, s, seed) {
    fillPoly(ctx, [[0, 0], [s, 0], [s, s], [0, s]], '#cfe0d5');
    fillPoly(ctx, [[0, s * 0.42], [s, s * 0.36], [s, s], [0, s]], '#bcd9e0');
    sline(ctx, 0, s * 0.4, s, s * 0.38, { color: PALETTE.water, width: 1.4, alpha: 0.7, seed: seed + 3, amp: 2 });
    for (let i = 0; i < 3; i++) {
      const x = rand(seed + i * 9) * s;
      const y = s * 0.55 + rand(seed + i * 13) * s * 0.4;
      scircle(ctx, x, y, 1.3, { color: PALETTE.stone, width: 0.8, alpha: 0.7, seed: seed + i, seg: 5, amp: 0.3 });
    }
  },

  /* ------------------------------ 道路 ------------------------------ */

  road(ctx, s, seed) {
    fillPoly(ctx, [[0, 0], [s, 0], [s, s], [0, s]], '#cdbb95');
    // 两道车辙
    sline(ctx, s * 0.28, 0, s * 0.28, s, { color: PALETTE.soilDark, width: 1.2, alpha: 0.35, seed: seed + 1, amp: 1.4 });
    sline(ctx, s * 0.72, 0, s * 0.72, s, { color: PALETTE.soilDark, width: 1.2, alpha: 0.35, seed: seed + 2, amp: 1.4 });
    for (let i = 0; i < 3; i++) {
      const x = rand(seed + i * 5) * s;
      const y = rand(seed + i * 9 + 4) * s;
      scircle(ctx, x, y, 1.1, { color: PALETTE.stone, width: 0.7, alpha: 0.6, seed: seed + i, seg: 5, amp: 0.3 });
    }
  },

  road_line(ctx, s, seed) {
    painters.road(ctx, s, seed);
    // 中间断续的白色车道线（竖向）
    const y0 = s * 0.22, y1 = s * 0.78;
    sline(ctx, s / 2, y0, s / 2, y1, { color: '#f4efe2', width: 2, alpha: 0.75, seed: seed + 21, amp: 0.6 });
  },

  bridge(ctx, s, seed) {
    fillPoly(ctx, [[0, 0], [s, 0], [s, s], [0, s]], '#bcd9e0');
    fillPoly(ctx, [[0, 0], [s, 0], [s, s], [0, s]], 'rgba(201,166,120,0.55)');
    for (let i = 0; i < 4; i++) {
      const y = i * (s / 4) + s / 8;
      sline(ctx, 1, y, s - 1, y, { color: PALETTE.woodDark, width: 1.2, alpha: 0.55, seed: seed + i, amp: 0.7 });
    }
    // 栏杆
    sline(ctx, 2, 0, 2, s, { color: PALETTE.woodDark, width: 2, alpha: 0.7, seed: seed + 31, amp: 0.5 });
    sline(ctx, s - 2, 0, s - 2, s, { color: PALETTE.woodDark, width: 2, alpha: 0.7, seed: seed + 32, amp: 0.5 });
  },

  checkpoint(ctx, s, seed) {
    fillPoly(ctx, [[0, 0], [s, 0], [s, s], [0, s]], '#ded9d0');
    srect(ctx, 0.5, 0.5, s - 1, s - 1, { color: PALETTE.inkSoft, width: 1, alpha: 0.4, seed: seed + 1 });
    for (let i = 0; i < 4; i++) {
      const y = 6 + i * 8;
      sline(ctx, 4, y, s - 4, y, { color: PALETTE.cloth, width: 2.4, alpha: 0.5, seed: seed + i, amp: 0.5 });
    }
  },

  /* ------------------------------ 城镇 ------------------------------ */

  sidewalk(ctx, s, seed) {
    fillPoly(ctx, [[0, 0], [s, 0], [s, s], [0, s]], '#e2dccd');
    for (let r = 0; r < 4; r++) {
      const y = r * (s / 4);
      sline(ctx, 0, y, s, y, { color: PALETTE.inkSoft, width: 0.9, alpha: 0.3, seed: seed + r, amp: 0.6 });
      const off = (r % 2) * (s / 4);
      sline(ctx, off, y, off, y + s / 4, { color: PALETTE.inkSoft, width: 0.9, alpha: 0.25, seed: seed + r * 5, amp: 0.5 });
    }
  },

  plaza(ctx, s, seed) {
    fillPoly(ctx, [[0, 0], [s, 0], [s, s], [0, s]], '#e7e1d2');
    // 四块大方石
    sline(ctx, s / 2, 0, s / 2, s, { color: PALETTE.inkSoft, width: 1, alpha: 0.3, seed: seed + 1, amp: 0.8 });
    sline(ctx, 0, s / 2, s, s / 2, { color: PALETTE.inkSoft, width: 1, alpha: 0.3, seed: seed + 2, amp: 0.8 });
    scircle(ctx, s * 0.28, s * 0.3, 1.4, { color: PALETTE.inkSoft, width: 0.7, alpha: 0.3, seed: seed + 3, seg: 5 });
    scircle(ctx, s * 0.74, s * 0.7, 1.2, { color: PALETTE.inkSoft, width: 0.7, alpha: 0.3, seed: seed + 4, seg: 5 });
  },

  building(ctx, s, seed) {
    fillPoly(ctx, [[0, 0], [s, 0], [s, s], [0, s]], '#e6dcc6');
    // 瓦檐 + 墙身，远看是一间铺面的一角
    fillPoly(ctx, [[0, 0], [s, 0], [s, s * 0.26], [0, s * 0.26]], '#c9b596');
    sline(ctx, 0, s * 0.26, s, s * 0.26, { color: PALETTE.ink, width: 1.4, alpha: 0.5, seed: seed + 1, amp: 0.8 });
    for (let i = 0; i < 3; i++) {
      const y = s * 0.4 + i * (s * 0.2);
      sline(ctx, 3, y, s - 3, y, { color: PALETTE.inkSoft, width: 0.9, alpha: 0.28, seed: seed + i * 3, amp: 0.6 });
    }
    // 一扇小窗
    srect(ctx, s * 0.3, s * 0.45, s * 0.4, s * 0.22, { color: PALETTE.woodDark, width: 1.2, alpha: 0.7, seed: seed + 11 });
  },
};

/**
 * 生成瓦片纹理缓存。
 * @returns {Map<string, HTMLCanvasElement[]>}
 */
export function buildTileCache(tileSize, variants = 4) {
  const cache = new Map();
  for (const key of Object.keys(painters)) {
    const list = [];
    for (let v = 0; v < variants; v++) {
      const cv = document.createElement('canvas');
      cv.width = tileSize;
      cv.height = tileSize;
      const c = cv.getContext('2d');
      painters[key](c, tileSize, v * 137 + 7);
      list.push(cv);
    }
    cache.set(key, list);
  }
  return cache;
}
